import { nextTick, ref } from "vue";

import { API } from "@/services/apiInstance";
import { resolveUniversalRecord } from "@/services/universalRecordIdentifier";
import {
    universalIntakeResolvers,
    universalRecordRoute
} from "@/services/universalRecordNavigation";
import { trackEvent } from "@/services/trackEvent";
import { store } from "@/store";

/** Emitter event any surface fires to open the global ScanLabelModal. */
export const SCAN_LABEL_OPEN = "scan-label:open";

/**
 * Scan-and-resolve flow for a Forkto tracking label, independent of where the
 * scanner is mounted. Lifted out of UniversalRecordIdentifyView so the same
 * behaviour (verify the record exists in this tenant, then hand back a route)
 * can drive a modal opened from anywhere.
 *
 * @param {import("vue").Ref} scanner   ref to a TrackingQrScanner instance
 * @param {object} options
 * @param {(destination: object) => Promise<void>|void} options.onResolved
 *        called with the route to open once the record is verified; the
 *        caller decides what to do with it (navigate, close a modal first…)
 */
export function useUniversalRecordScan(scanner, { onResolved } = {}) {
    const resolving = ref(false);
    const errorMessage = ref("");
    // Set once a record has been handed off. The camera stops itself on
    // decode, but nothing else should be able to restart the flow until the
    // caller explicitly resets.
    const handled = ref(false);

    async function startScanning({ preserveError = false } = {}) {
        if (!preserveError) errorMessage.value = "";
        handled.value = false;
        await nextTick();
        return scanner.value?.start();
    }

    function reset() {
        resolving.value = false;
        errorMessage.value = "";
        handled.value = false;
    }

    function stopScanning() {
        scanner.value?.stop();
    }

    async function identifyRecord(identifier) {
        if (resolving.value || handled.value) return;
        resolving.value = true;
        errorMessage.value = "";

        try {
            // A printed product label names its tenant. Refuse one from another
            // account outright rather than looking the id up here, where it
            // would either 404 or, worse, collide with an unrelated record.
            const currentTenant = store.getters.customerID?.() || store.state.customerID;
            if (
                identifier.labelTenant &&
                currentTenant &&
                identifier.labelTenant !== currentTenant
            ) {
                const err = new Error("Label belongs to a different Forkto account");
                err.code = "wrong_tenant";
                throw err;
            }
            const record = await resolveUniversalRecord(identifier, {
                resolvers: universalIntakeResolvers(API)
            });
            if (!record?.id && !record?._id) throw new Error("Record not found");

            const destination = universalRecordRoute(identifier);
            trackEvent("universal-record-identified", {
                record_type: identifier.recordType,
                source: identifier.source || "unknown"
            });
            handled.value = true;
            await onResolved?.(destination);
        } catch (error) {
            const code = error?.code;
            errorMessage.value =
                code === "unsupported_record_type"
                    ? "This Forkto label is valid, but this record type cannot be opened from the scanner yet."
                    : code === "wrong_tenant"
                      ? `This label belongs to a different Forkto account (${identifier.labelTenant}).`
                      : "This record could not be found in the current Forkto account.";
            trackEvent("universal-record-identification-failed", {
                reason: error?.code || "record_not_found",
                record_type: identifier?.recordType || "unknown"
            });
            // Do not restart the camera here. The failed code is still in
            // frame, so an automatic restart re-decodes it within ~100ms and
            // the failure loops with a beep each time. The caller shows the
            // error and offers an explicit "scan again".
        } finally {
            resolving.value = false;
        }
    }

    function onScanError({ code }) {
        trackEvent("universal-record-identification-failed", {
            reason: code || "scanner_error"
        });
    }

    return {
        resolving,
        errorMessage,
        handled,
        startScanning,
        stopScanning,
        reset,
        identifyRecord,
        onScanError
    };
}
